import type { IpAllowlistResponse } from '@/types/api-types';
/**
 * 管理画面関連フック
 *
 * 組織設定、ユーザー管理、サブスクリプション、監査ログ、ロール管理。
 * useFetch / useMutation パターンに準拠。
 */
import { useMemo, useCallback, useState } from 'react';
import { apiFetch } from '@/api/client';
import { useFetch, useMutation } from '@/hooks/useApi';
import {
  classifyWhitelistSaveError,
  type WhitelistPutPayload,
} from '@/utils/requesterAddableWhitelist';
import type {
  Organization,
  AdminUser,
  CurrentUserProfile,
  AssignableUser,
  Subscription,
  AuditLogListResponse,
  AuditLogSummary,
  AdminRole,
  DataScope,
  AreaScope,
  PermissionGrant,
  ApiListResponse,
  BulkInviteResult,
  BulkInviteRow,
  UserRole,
  DocumentCategory,
  RequesterAddableAdminResponse,
} from '@/types';

// ==============================
// 入力型定義
// ==============================

/** 組織更新入力 */
interface UpdateOrganizationInput {
  orgName?: string;
  contactEmail?: string;
  contactPhone?: string;
  faxNumber?: string;
  postalCode?: string;
  address?: string;
  /** steering 414 A21: 事務所建物名 (受任通知の差出人ブロック用) */
  buildingName?: string;
  representativeName?: string;
  /** ロゴ URL。null を指定するとロゴを削除する */
  logoUrl?: string | null;
}

/** ユーザー作成入力 */
interface CreateUserInput {
  phoneNumber?: string;
  email: string;
  displayName: string;
  role: UserRole;
  position?: string | null;
}

/** ユーザー更新入力 */
interface UpdateUserInput {
  userId: string;
  data: {
    displayName?: string;
    role?: UserRole;
    /**
     * ロール変更の冪等キー (UUID v4、steering 565)。**ロールを変えるときだけ**付ける。
     * 1 回の変更操作につき 1 つ生成し、同じ操作の再送では同じ値を使う (BE が 1 操作 1 監査に固定する)。
     */
    idempotencyKey?: string;
    position?: string | null;
    /**
     * プロフィール写真の S3 key (user-profile-avatar-20260828)。
     * **key を省略すると写真は変更されない** / `null` で解除 / string で更新。
     * string は `avatar/upload-url` が返した key を S3 PUT 成功後にそのまま渡す
     * (BE が実 object を HeadObject で検証してから確定する)。
     */
    avatarKey?: string | null;
  };
}

/**
 * 監査ログ検索パラメータ (steering 460)
 *
 * BE `GET /admin/audit-logs` が実際に受理する query 名と **1:1** で揃える。
 * 旧実装は `dateFrom` / `dateTo` を送っていたが BE は `from` / `to` しか読まないため、
 * 期間条件が黙って捨てられていた (SPEC §9.6.6 も `from` / `to`)。
 * `search` は BE 未実装のため送らない (取得済みページ内の FE 絞り込みで維持する)。
 */
export interface AuditLogSearchParams {
  category?: string;
  severity?: string;
  userId?: string;
  /** UTC ISO8601。JST 日界から正規化した値を渡す */
  from?: string;
  /** UTC ISO8601。日付指定時は当日 23:59:59.999 JST 相当まで含める */
  to?: string;
  limit?: number;
  cursor?: string;
}

// ==============================
// 組織設定
// ==============================

/** 組織情報を取得する */
export function useOrganization(enabled = true) {
  return useFetch<Organization>(enabled ? '/admin/organization' : null);
}

/** 組織情報を更新する */
export function useUpdateOrganization() {
  const fn = useCallback(
    (input: UpdateOrganizationInput) =>
      apiFetch<Organization>('/admin/organization', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    [],
  );
  return useMutation<UpdateOrganizationInput, Organization>(fn);
}

// ==============================
// ユーザー管理
// ==============================

/**
 * ユーザー一覧を取得する。
 *
 * `enabled=false` なら fetch しない (既定 true で従来の呼び出しは無変更)。
 * この endpoint は `userMgmt:read` を要求するため、権限を持たない principal から
 * 無条件に呼ぶと 403 になる。呼び出し側で `canRead('userMgmt')` を渡して gate する
 * (steering 460: 監査ログのユーザー絞り込みは orgSettings:read だけの利用者もいる)。
 */
export function useAdminUsers(enabled = true) {
  return useFetch<ApiListResponse<AdminUser>>(enabled ? '/admin/users' : null);
}

/**
 * ログイン中ユーザー本人のプロフィールを取得する (GET /admin/users/me)。
 *
 * userMgmt 権限は不要な self-scoped endpoint。ヘッダーのユーザーメニューが氏名
 * (displayName) を表示するために使用する。enabled=false (未ログイン等) の場合は
 * fetch しない。取得失敗時も画面を壊さず、呼び出し側の表示 fallback に委ねる。
 */
export function useCurrentUserProfile(enabled = true) {
  return useFetch<CurrentUserProfile>(enabled ? '/admin/users/me' : null);
}

/**
 * ログイン中ユーザー自身のプロフィールが更新されたことを知らせる CustomEvent 名
 * (initiative: user-profile-avatar-20260828)。
 *
 * `useFetch` は doc comment に「SWR 風」とあるが **実体は component ごとの独立 state で
 * 共有 cache も global mutate も持たない**。そのためユーザー管理画面の `refetch()` は
 * ヘッダーの `useCurrentUserProfile` に届かず、自分の写真を変えても右上が古いままになる。
 *
 * 既に repo にある `PREFS_CHANGED_EVENT` (`hooks/useNotificationPopup.ts`) と同型の
 * CustomEvent で橋渡しする。新しい状態管理ライブラリは導入しない。
 *
 * detail には **更新対象の userId** を載せる。ヘッダー側は「自分が更新されたときだけ」
 * 再取得し、他ユーザーの編集では発火しない。
 */
export const CURRENT_USER_PROFILE_CHANGED_EVENT = 'acecollect:current-user-profile-changed';

/** {@link CURRENT_USER_PROFILE_CHANGED_EVENT} の detail 形状。 */
export interface CurrentUserProfileChangedDetail {
  userId: string;
}

/** プロフィール更新を全 component へ通知する (対象 userId 付き)。 */
export function notifyUserProfileChanged(userId: string): void {
  window.dispatchEvent(
    new CustomEvent<CurrentUserProfileChangedDetail>(CURRENT_USER_PROFILE_CHANGED_EVENT, {
      detail: { userId },
    }),
  );
}

/**
 * プロフィール写真アップロード用の pre-signed URL を取得する
 * (POST /admin/users/{userId}/avatar/upload-url)。
 *
 * 返る `key` は S3 PUT 成功後に `useUpdateUser` の `avatarKey` として渡し、
 * BE 側で実 object を検証してから DB へ確定する (3-step。SPEC §4.5)。
 */
export function useAvatarUploadUrl() {
  const fn = useCallback(
    (input: { userId: string; contentType: string; fileSize: number }) =>
      apiFetch<{ uploadUrl: string; key: string }>(
        `/admin/users/${input.userId}/avatar/upload-url`,
        {
          method: 'POST',
          body: JSON.stringify({ contentType: input.contentType, fileSize: input.fileSize }),
        },
      ),
    [],
  );
  return useMutation<
    { userId: string; contentType: string; fileSize: number },
    { uploadUrl: string; key: string }
  >(fn);
}

/**
 * 案件作成・担当者選択用の最小ユーザー候補を取得する (#B2)。
 *
 * GET /admin/users/assignable。認可は BE 側で collections/write (userMgmt は不要)。
 * 返却は PII を含まない AssignableUser のみ。ユーザー管理 (useAdminUsers / userMgmt) とは
 * 別エンドポイントで、member でも担当弁護士/事務員の候補を取得できる。
 */
export function useAssignableUsers(enabled = true) {
  return useFetch<ApiListResponse<AssignableUser>>(enabled ? '/admin/users/assignable' : null);
}

/** ユーザーを作成する */
export function useCreateUser() {
  const fn = useCallback(
    (input: CreateUserInput) =>
      apiFetch<AdminUser>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    [],
  );
  return useMutation<CreateUserInput, AdminUser>(fn);
}

/** ユーザーを更新する */
export function useUpdateUser() {
  const fn = useCallback(
    (input: UpdateUserInput) =>
      apiFetch<AdminUser>(`/admin/users/${input.userId}`, {
        method: 'PUT',
        body: JSON.stringify(input.data),
      }),
    [],
  );
  return useMutation<UpdateUserInput, AdminUser>(fn);
}

/** ユーザーを有効化する */
export function useEnableUser() {
  const fn = useCallback(
    (userId: string) =>
      apiFetch<Record<string, never>>(`/admin/users/${userId}/enable`, {
        method: 'PUT',
      }),
    [],
  );
  return useMutation<string, Record<string, never>>(fn);
}

/** ユーザーを無効化する */
export function useDisableUser() {
  const fn = useCallback(
    (userId: string) =>
      apiFetch<Record<string, never>>(`/admin/users/${userId}/disable`, {
        method: 'PUT',
      }),
    [],
  );
  return useMutation<string, Record<string, never>>(fn);
}

/**
 * パスワードをリセットする
 *
 * QA-160 短期回避 (案 C): BE の AdminSetUserPasswordCommand は Cognito Custom Email Sender を
 * 起動しないため、レスポンスに含まれる `tempPassword` を FE で一度だけ admin に表示する。
 * tempPassword は localStorage / sessionStorage / global state / console には絶対に書かない。
 */
export interface ResetPasswordResult {
  reset: boolean;
  tempPassword: string;
}
export function useResetPassword() {
  const fn = useCallback(
    (userId: string) =>
      apiFetch<ResetPasswordResult>(`/admin/users/${userId}/reset-password`, {
        method: 'PUT',
      }),
    [],
  );
  return useMutation<string, ResetPasswordResult>(fn);
}

/** メールアドレスを変更する (user-management-email-change-delete-20260910)。ログインは新アドレス、userId は不変。 */
export interface UpdateUserEmailInput {
  userId: string;
  email: string;
}
export function useUpdateUserEmail() {
  const fn = useCallback(
    (input: UpdateUserEmailInput) =>
      apiFetch<AdminUser>(`/admin/users/${input.userId}/email`, {
        method: 'PUT',
        body: JSON.stringify({ email: input.email }),
      }),
    [],
  );
  return useMutation<UpdateUserEmailInput, AdminUser>(fn);
}

/** ユーザーを削除する (同 initiative)。Owner と本人は BE が 400 で拒否する。 */
export interface DeleteUserResult {
  deleted: boolean;
  userId: string;
}
export function useDeleteUser() {
  const fn = useCallback(
    (userId: string) =>
      apiFetch<DeleteUserResult>(`/admin/users/${userId}`, {
        method: 'DELETE',
      }),
    [],
  );
  return useMutation<string, DeleteUserResult>(fn);
}

/** 一括招待する */
export function useBulkInvite() {
  const fn = useCallback(
    (rows: BulkInviteRow[]) =>
      apiFetch<BulkInviteResult>('/admin/users/bulk-invite', {
        method: 'POST',
        body: JSON.stringify({ users: rows }),
      }),
    [],
  );
  return useMutation<BulkInviteRow[], BulkInviteResult>(fn);
}

// ==============================
// サブスクリプション
// ==============================

/** サブスクリプション情報を取得する */
export function useSubscription() {
  return useFetch<Subscription>('/admin/subscription');
}

// ==============================
// 監査ログ
// ==============================

/**
 * 監査ログ検索パラメータから query string を組み立てる (一覧 / CSV で共用)。
 *
 * CSV エクスポートは BE が `from` / `to` / `category` のみ受理するため、
 * `includePaging` = false で severity / userId / limit / cursor を落とす。
 */
export function buildAuditLogQuery(
  params: AuditLogSearchParams | undefined,
  options: { includePaging?: boolean } = {},
): string {
  const { includePaging = true } = options;
  const qs = new URLSearchParams();
  if (params?.category) qs.set('category', params.category);
  if (params?.from) qs.set('from', params.from);
  if (params?.to) qs.set('to', params.to);
  if (includePaging) {
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.userId) qs.set('userId', params.userId);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.cursor) qs.set('cursor', params.cursor);
  }
  return qs.toString();
}

/** 監査ログ一覧を取得する */
export function useAuditLogs(params?: AuditLogSearchParams) {
  const url = useMemo(() => {
    const query = buildAuditLogQuery(params);
    return `/admin/audit-logs${query ? `?${query}` : ''}`;
  }, [
    params?.category,
    params?.severity,
    params?.userId,
    params?.from,
    params?.to,
    params?.limit,
    params?.cursor,
  ]);

  return useFetch<AuditLogListResponse>(url);
}

/**
 * 監査ログ KPI 集計を取得する (steering 460)。
 *
 * 一覧 API は総件数を返さないため、取得済みページから KPI を数えると偽集計になる。
 * 集計専用 endpoint が当日 / 前日同時刻を全件走査した exact な値を返す。
 */
export function useAuditLogSummary() {
  return useFetch<AuditLogSummary>('/admin/audit-logs/summary');
}

// ==============================
// ロール管理
// ==============================

/** ロール DTO として必要な項目が揃っているか。 */
function isAdminRole(value: unknown): value is AdminRole {
  if (typeof value !== 'object' || value === null) return false;
  const r = value as Record<string, unknown>;
  return (
    typeof r.roleId === 'string' &&
    typeof r.roleName === 'string' &&
    typeof r.editable === 'boolean' &&
    typeof r.version === 'number' &&
    typeof r.permissions === 'object' &&
    r.permissions !== null &&
    typeof r.permissionSources === 'object' &&
    r.permissionSources !== null
  );
}

/**
 * `GET /admin/roles` の封筒を検証する (steering 431 Gate 12-R2)。
 *
 * **形が違うものを空配列に丸めない。** 丸めると「まだ 1 件も無い」と
 * 「レスポンスが想定と違う」が画面上まったく同じ見え方になり、
 * 機能が丸ごと表示されないことに誰も気づけない。
 */
function parseAdminRolesResponse(
  data: unknown,
): { ok: true; roles: AdminRole[] } | { ok: false; message: string } {
  if (typeof data !== 'object' || data === null) {
    return { ok: false, message: 'ロール一覧のレスポンス形式が想定と異なります' };
  }
  const roles = (data as Record<string, unknown>).roles;
  if (roles === undefined) {
    return { ok: false, message: 'ロール一覧のレスポンスに roles が含まれていません' };
  }
  if (!Array.isArray(roles)) {
    return { ok: false, message: 'ロール一覧のレスポンスの roles が配列ではありません' };
  }
  if (!roles.every(isAdminRole)) {
    return { ok: false, message: 'ロール一覧のレスポンスに不足している項目があります' };
  }
  return { ok: true, roles };
}

/**
 * ロール一覧を取得する。
 *
 * BE は `{ roles: [...] }` を返す。**汎用の一覧型 (`items`) を当てはめない**
 * (当てはめた結果、画面が常に空配列を見て editor ごと描画されなかった)。
 * 通信エラーと契約違反は、いずれも `error` として画面へ出す。
 */
export function useAdminRoles(): {
  roles: AdminRole[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const { data, loading, error, refetch } = useFetch<unknown>('/admin/roles');
  const parsed = useMemo(
    () => (data === null || data === undefined ? null : parseAdminRolesResponse(data)),
    [data],
  );
  const contractError = parsed !== null && !parsed.ok ? parsed.message : null;
  return {
    roles: parsed !== null && parsed.ok ? parsed.roles : [],
    loading,
    // 通信エラーを優先し、無ければ契約違反を出す
    error: error ?? contractError,
    refetch,
  };
}

/** ロール権限の更新入力 (steering 431)。**変更した area だけ送る**。 */
export interface UpdateRolePermissionsInput {
  roleId: string;
  expectedVersion: number;
  /** area ごとの付与内容。null は「組織設定を外して継承 / 既定へ戻す」。 */
  changes: Record<string, PermissionGrant | null>;
  /**
   * permission-area-data-scope: 項目ごとの閲覧範囲。null は「上書きを外してロールの既定に従う」。
   * 範囲を変えた項目だけ送る。
   */
  areaDataScopes?: Record<string, AreaScope | null>;
}

/**
 * ロール権限の更新 (steering 431)。
 * 競合は 409、owner の編集と自己ロックアウトは 400 で返る。
 */
/** steering 565: カスタムロール作成の入力 (design §3.2)。`roleId` は **送らない**。 */
export interface CreateRoleInput {
  roleName: string;
  description?: string;
  dataScope?: DataScope;
}

/**
 * カスタムロールを作成する (`POST /admin/roles`)。
 *
 * `roleId` はサーバが `custom_` + UUID で採番する。**client からは指定できない**
 * (既定ロールと名前空間が混ざると `owner` 詐称の余地ができる)。
 */
export function useCreateRole() {
  const fn = useCallback(
    (body: CreateRoleInput) =>
      apiFetch<AdminRole>('/admin/roles', { method: 'POST', body: JSON.stringify(body) }),
    [],
  );
  return useMutation<CreateRoleInput, AdminRole>(fn);
}

/** steering 565: ロールのメタデータ更新の入力 (design §3.3)。権限マトリクスとは別経路。 */
export interface UpdateRoleMetadataInput {
  roleId: string;
  expectedVersion: number;
  roleName?: string;
  description?: string;
  dataScope?: DataScope;
}

/**
 * ロールの名称 / 説明 / 閲覧範囲を更新する (`PUT /admin/roles/{roleId}/metadata`)。
 *
 * 権限マトリクスの保存とは **別 route**。監査の粒度を分けるため。
 * `expectedVersion` 不一致は 409 で、**自動再試行しない** (画面が再読み込み導線を出す)。
 */
export function useUpdateRoleMetadata() {
  const fn = useCallback(
    ({ roleId, ...body }: UpdateRoleMetadataInput) =>
      apiFetch<AdminRole>(`/admin/roles/${roleId}/metadata`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    [],
  );
  return useMutation<UpdateRoleMetadataInput, AdminRole>(fn);
}

/**
 * カスタムロールを削除する (`DELETE /admin/roles/{roleId}`)。
 *
 * 使用中 (利用者が居る / 未解決の割当がある) なら **409**。既定ロールは 400。
 * どちらも**自動再試行しない** — 状態が変わらない限り結果は同じである。
 */
export function useDeleteRole() {
  const fn = useCallback(
    (roleId: string) =>
      apiFetch<{ roleId: string }>(`/admin/roles/${roleId}`, { method: 'DELETE' }),
    [],
  );
  return useMutation<string, { roleId: string }>(fn);
}

export function useUpdateRolePermissions() {
  const fn = useCallback(
    ({ roleId, ...body }: UpdateRolePermissionsInput) =>
      apiFetch<AdminRole>(`/admin/roles/${roleId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    [],
  );
  return useMutation<UpdateRolePermissionsInput, AdminRole>(fn);
}

// ==============================
// SSO 管理
// ==============================

/** SSO 設定の FE 型 */
interface SsoConfigResponse {
  ssoConfig: {
    enabled: boolean;
    provider: string;
    workosOrganizationId: string;
    workosConnectionId: string;
    workosConnectionType: 'SAML' | 'OIDC';
    idpName: string;
    emailDomain: string;
    jitProvisioningEnabled: boolean;
    defaultRole: 'admin' | 'member' | 'viewer';
    createdAt: string;
    updatedAt: string;
  } | null;
}

/** SSO 設定取得 */
export function useSsoConfig() {
  return useFetch<SsoConfigResponse>('/admin/sso');
}

/** SSO セットアップ */
export function useSsoSetup() {
  const fn = useCallback(
    (input: { provider: string; domain: string; clientId: string; clientSecret: string }) =>
      apiFetch('/admin/sso/setup', { method: 'POST', body: JSON.stringify(input) }),
    [],
  );
  return useMutation(fn);
}

/** SSO テスト接続 */
export function useSsoTest() {
  const fn = useCallback(() => apiFetch('/admin/sso/test', { method: 'POST' }), []);
  return useMutation(fn);
}

// ==============================
// IP アドレス制限
// ==============================

/** IP 制限設定取得 */
export function useIpAllowlist() {
  return useFetch<IpAllowlistResponse>('/admin/security/ip-allowlist');
}

/** IP 制限設定更新 */
export function useUpdateIpAllowlist() {
  const fn = useCallback(
    (input: { enabled: boolean; allowedCidrs: { cidr: string; label: string }[] }) =>
      apiFetch('/admin/security/ip-allowlist', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    [],
  );
  return useMutation(fn);
}

/** セキュリティセッション一覧 */
export function useSecuritySessions() {
  return useFetch<{ items: { userId: string; email: string; lastActive: string; ip: string }[] }>(
    '/admin/security/sessions',
  );
}

/** セッション強制ログアウト */
export function useRevokeSession() {
  const fn = useCallback(
    (userId: string) => apiFetch(`/admin/security/sessions/${userId}/revoke`, { method: 'POST' }),
    [],
  );
  return useMutation(fn);
}

// ==============================
// 書類カテゴリ動的管理 (steering #232)
// ==============================

/**
 * 書類カテゴリ一覧を取得する (各カテゴリの usageCount 付き)
 *
 * GET /admin/settings/document-categories
 * settings:read 権限が必要 (member/viewer も読取可)
 */
export function useDocumentCategories(enabled = true) {
  return useFetch<{ categories: DocumentCategory[] }>(
    enabled ? '/admin/settings/document-categories' : null,
  );
}

/**
 * 書類カテゴリを全置換更新する (owner/admin のみ)
 *
 * PUT /admin/settings/document-categories
 * バリデーション: 1〜20 件、id/label 重複なし、削除/リネーム時の使用件数チェック
 */
export function useUpdateDocumentCategories() {
  const fn = useCallback(
    (categories: DocumentCategory[]) =>
      apiFetch<{ categories: DocumentCategory[] }>('/admin/settings/document-categories', {
        method: 'PUT',
        // usageCount は PUT 時に送らない (BE が再計算)
        body: JSON.stringify({
          categories: categories.map(({ id, label, order }) => ({ id, label, order })),
        }),
      }),
    [],
  );
  return useMutation<DocumentCategory[], { categories: DocumentCategory[] }>(fn);
}

// ==============================
// 依頼者追加可能書類 whitelist 管理 (steering 328 / 19-D-F1)
// ==============================

/**
 * 依頼者追加可能書類 whitelist を取得する (有効 + 無効の全 option、version 付き)。
 *
 * GET /admin/settings/requester-addable-documents
 * settings:read 権限が必要 (member/viewer も読取可)。未作成時は version=0 / options=[]。
 */
export function useRequesterAddableDocuments() {
  return useFetch<RequesterAddableAdminResponse>('/admin/settings/requester-addable-documents');
}

/** whitelist 保存の結果 (version 楽観ロックの 409 を区別する) */
export type WhitelistSaveResult =
  | { status: 'ok'; data: RequesterAddableAdminResponse }
  | { status: 'conflict' }
  | { status: 'forbidden' }
  | { status: 'error'; message: string };

/**
 * whitelist を全置換更新する (orgSettings:write のみ)。
 *
 * PUT /admin/settings/requester-addable-documents ({ expectedVersion, options })。
 * version 不一致 / 並行更新は 409 (code=CONFLICT) を `status: 'conflict'` として返し、
 * 呼び出し側が再取得を促す。403 は client が global トーストを出すため `forbidden` で区別する。
 * (useMutation を使わず bespoke なのは、409 を成功/失敗の二値でなく専用ハンドリングするため)
 */
export function useUpdateRequesterAddableDocuments() {
  const [saving, setSaving] = useState(false);

  const mutate = useCallback(async (payload: WhitelistPutPayload): Promise<WhitelistSaveResult> => {
    setSaving(true);
    try {
      const data = await apiFetch<RequesterAddableAdminResponse>(
        '/admin/settings/requester-addable-documents',
        { method: 'PUT', body: JSON.stringify(payload) },
      );
      return { status: 'ok', data };
    } catch (err) {
      const kind = classifyWhitelistSaveError(err);
      if (kind === 'conflict') return { status: 'conflict' };
      if (kind === 'forbidden') return { status: 'forbidden' };
      const message = err instanceof Error ? err.message : '保存に失敗しました';
      return { status: 'error', message };
    } finally {
      setSaving(false);
    }
  }, []);

  return { mutate, saving };
}
